import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type {
  CombatState,
  UIState,
  HighlightedCell,
  Spell,
  CombatEntity,
} from '../types/interfaces';

// ─────────────────────────────────────────────────────────────────────────────
// Store Types
// ─────────────────────────────────────────────────────────────────────────────

/** Sequência de células percorridas por uma entidade na última ação (server). */
export type EntityMovement = { entity_id: string; path: Array<{ x: number; y: number }> };

interface CombatStore {
  // Server state
  combat: CombatState | null;

  /**
   * Caminhos percorridos na ÚLTIMA atualização de estado (só quando a ação
   * moveu alguém). Consumido pelo CombatGrid para animar o passo-a-passo.
   * Zerado a cada `setCombat` que não traz movimentos.
   */
  lastMovements: EntityMovement[];

  // Local UI state
  ui: UIState;

  // Spell cache (keyed by ID)
  spellCache: Record<string, Spell>;

  // WebSocket connection
  wsConnected: boolean;

  // Error message sent by the server (e.g. "Combat not found")
  serverError: string | null;

  // ── Actions ────────────────────────────────────────────────────────────────
  setCombat: (state: CombatState, movements?: EntityMovement[]) => void;
  resetCombat: () => void;
  /**
   * Locally marks the current combat as ABANDONED.
   * This is a client-side-only stamp — the server is not notified.
   * Always pair with disconnect() so the WebSocket is closed immediately.
   */
  abandonCombat: () => void;
  setMyEntityId: (id: string) => void;
  setWsConnected: (v: boolean) => void;
  setServerError: (msg: string | null) => void;

  selectSpell: (spellId: string | null) => void;
  setCellHover: (x: number, y: number) => void;
  clearHover: () => void;
  setHighlights: (cells: HighlightedCell[]) => void;
  clearHighlights: () => void;

  addToSpellCache: (spells: Spell[]) => void;

  // ── Selectors (derived) ────────────────────────────────────────────────────
  getMyEntity: () => CombatEntity | null;
  getActiveEntity: () => CombatEntity | null;
  isMyTurn: () => boolean;
  getSelectedSpell: () => Spell | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store Creation
// ─────────────────────────────────────────────────────────────────────────────

export const useCombatStore = create<CombatStore>()(
  subscribeWithSelector((set, get) => ({
    combat: null,
    lastMovements: [],
    ui: {
      selectedSpellId: null,
      highlights: [],
      cellHover: null,
      myEntityId: null,
    },
    spellCache: {},
    wsConnected: false,
    serverError: null,

    // ── Reset all combat state (call before entering a new battle) ─────────
    resetCombat: () =>
      set({
        combat: null,
        lastMovements: [],
        serverError: null,
        // wsConnected NÃO é zerado aqui: o estado da conexão vem só dos eventos
        // do socket (onopen/onclose/disconnect). Zerar ao trocar de combate com o
        // socket aberto deixava a tela em "desconectado" com as mensagens chegando.
        spellCache: {},
        ui: {
          selectedSpellId: null,
          highlights: [],
          cellHover: null,
          myEntityId: null,
        },
      }),

    // ── Abandon (client-side only — not sent to server) ────────────────────
    abandonCombat: () =>
      set((s) => ({
        combat: s.combat ? { ...s.combat, phase: 'ABANDONED' } : null,
      })),

    // ── Update state from server ────────────────────────────────────────────
    setCombat: (state, movements) => {
      // Extract spells embedded in entities into the global cache.
      // Guard: MongoDB may return nested arrays as plain objects (e.g.
      // {"0": spell0, "1": spell1}) instead of real JS arrays — convert both.
      const newCache: Record<string, Spell> = {};
      for (const entity of Object.values(state.entities ?? {})) {
        const rawSpells = entity.spells;
        const spellList: Spell[] = Array.isArray(rawSpells)
          ? rawSpells
          : rawSpells != null ? Object.values(rawSpells as Record<string, Spell>) : [];
        for (const spell of spellList) {
          if (spell?.id) newCache[spell.id] = spell;
        }
      }
      set((s) => ({
        combat: state,
        lastMovements: movements ?? [],
        spellCache: { ...s.spellCache, ...newCache },
      }));
    },

    setMyEntityId: (id) =>
      set((s) => ({ ui: { ...s.ui, myEntityId: id } })),

    setWsConnected: (v) => set({ wsConnected: v }),

    setServerError: (msg) => set({ serverError: msg }),

    // ── Spell selection and highlights ─────────────────────────────────────
    selectSpell: (spellId) =>
      set((s) => ({
        ui: {
          ...s.ui,
          selectedSpellId: spellId,
          highlights: spellId ? s.ui.highlights : [],
        },
      })),

    setCellHover: (x, y) =>
      set((s) => ({ ui: { ...s.ui, cellHover: { x, y } } })),

    clearHover: () =>
      set((s) => ({ ui: { ...s.ui, cellHover: null } })),

    setHighlights: (cells) =>
      set((s) => ({ ui: { ...s.ui, highlights: cells } })),

    clearHighlights: () =>
      set((s) => ({ ui: { ...s.ui, highlights: [] } })),

    addToSpellCache: (spells) =>
      set((s) => ({
        spellCache: {
          ...s.spellCache,
          ...Object.fromEntries(spells.map((sp) => [sp.id, sp])),
        },
      })),

    // ── Selectors ──────────────────────────────────────────────────────────
    getMyEntity: () => {
      const { combat, ui } = get();
      if (!combat || !ui.myEntityId) return null;
      return combat.entities[ui.myEntityId] ?? null;
    },

    getActiveEntity: () => {
      const { combat } = get();
      if (!combat) return null;
      return combat.entities[combat.active_entity_id] ?? null;
    },

    isMyTurn: () => {
      const { combat, ui } = get();
      if (!combat || !ui.myEntityId) return false;
      return combat.active_entity_id === ui.myEntityId;
    },

    getSelectedSpell: () => {
      const { ui, spellCache } = get();
      if (!ui.selectedSpellId) return null;
      return spellCache[ui.selectedSpellId] ?? null;
    },
  }))
);
